import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { find, has, isEmpty, isNil } from 'lodash';
import { getCurrentOrg, getCurrentUser } from 'js/reducers/session';
import { getAllDomainEntities } from 'js/reducers/entities/selectors';

type Org = {
  id: number;
  name: string;
  description: string;
  cluster_id: number;
  new_cluster_id: number | null;
};

type OrgMembership = {
  cluster_status: any;
  id: number;
  name: string;
  is_admin?: true;
  access_role: string;
  org_membership_status: string;
};
interface UseCurrentUser {
  account_locked: boolean;
  apiKey: string | null;
  created_at: string;
  default_org: number;
  email: string;
  email_verified_at: string;
  full_name: string;
  id: number;
  impersonated: boolean;
  isAdmin: boolean;
  isOrgAdmin: boolean;
  isOrgCustodian: boolean;
  isDomainCustodian: boolean;
  org: Org;
  status: string;
  super_user: boolean;
  token: string;
  tos_signed_at: string | null;
  updated_at: string;
  user_tier: string | null;
  org_memberships: OrgMembership[];
  custodian_for_domains: number[];
  custodian_for_orgs: number[];
}

export function useCurrentUser(): UseCurrentUser {
  const user = useSelector(getCurrentUser);
  const currentOrg = useSelector(getCurrentOrg);
  const currentDomains = useSelector(getAllDomainEntities);

  return useMemo(() => {
    const orgMembership =
      has(user, 'org_memberships') && !isEmpty(user.org_memberships)
        ? find(user.org_memberships, (o) => {
            return o.id === currentOrg.id;
          })
        : null;

    const isOrgAdmin =
      !isNil(orgMembership) && has(orgMembership, 'is_admin?') ? orgMembership['is_admin?'] : false;

    const isOrgCustodian = user?.custodian_for_orgs?.some((o) => o.id === currentOrg.id);

    const isDomainCustodian = user?.custodian_for_domains?.some((id) =>
      currentDomains.hasOwnProperty(id),
    );

    return {
      ...user,
      org: currentOrg,
      isAdmin: isOrgAdmin,
      isOrgAdmin,
      isOrgCustodian,
      isDomainCustodian,
    };
  }, [user, currentOrg, currentDomains]);
}
